/**
 * One-shot gate for publisher-supplied page metadata.
 *
 * When `waitForPageMetaData` is enabled, ConcertAds holds Permutive's page-data event until the
 * page calls `setPageMetaData({ page, article })`. That metadata is then the source of truth for
 * the Permutive `page`/`article` properties (see {@link getPageData}). When the flag is off the
 * gate never blocks, so the existing variable-derived behavior is unchanged.
 *
 * Turning the flag on is a contract: the application MUST call `setPageMetaData`. There is no
 * backstop timeout — if it never calls, the page-data event (and the Permutive setup that follows
 * it) intentionally never fires.
 */
export default class PageMetaData {
  /**
   * @param {{ app: ConcertAds }}
   */
  constructor({ app }) {
    this.app = app;
    this._data = {};

    // One-shot deferred: capture the resolver so set() can signal readiness. The metadata itself
    // is read live via get() (not carried on the promise), so a later/replacement set() — e.g. SPA
    // navigation or reinstall — is reflected instead of a stale first snapshot.
    this._promise = new Promise(resolve => {
      this._resolve = resolve;
    });
  }

  /**
   * Whether ConcertAds should wait for setPageMetaData before sending Permutive page data.
   * @return {boolean}
   */
  get enabled() {
    return !!this.app.settings.waitForPageMetaData;
  }

  /**
   * Provide the page metadata. Stores only `page` and `article` (any other keys are ignored) and
   * signals the gate is ready (read the data via {@link get}). Called via
   * {@link ConcertAds#setPageMetaData} (available from construction) or
   * {@link PmcAdBridge#setPageMetaData} (once the bridge chunk loads). Safe to call more than once:
   * the latest data wins and is what {@link get} returns.
   * @param {{ page?: object, article?: object }} [data]
   */
  set(data = {}) {
    this._data = { page: data.page, article: data.article };
    this._resolve();
  }

  /**
   * The stored page metadata.
   * @return {{ page?: object, article?: object }}
   */
  get() {
    return this._data;
  }

  /**
   * Readiness signal (resolves with no value — read the metadata via {@link get} after awaiting).
   * When disabled we never block and resolve immediately, so the variable-derived path runs
   * unchanged; when enabled, resolves once set() first supplies the data. Reading get() after the
   * await (rather than a value carried on the promise) is what lets a later/replacement set() be
   * reflected instead of a stale first snapshot.
   * @return {Promise<void>}
   */
  whenReady() {
    if (!this.enabled) return Promise.resolve();
    return this._promise;
  }
}
